import { useParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import { Button } from '@/components/ui/button';
import { BookText, ExternalLink } from 'lucide-react';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { DocLinkCardHorizontal } from '@/pages/dashboard/serverless-gpu/detail/overview/doc';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useListSSHInstances } from '@/service/gpu-instance/hook';

// OCR API Configuration
const OCR_DOCS_URL = 'https://docs.opentyphoon.ai/typhoon-ocr';

const OCRApiEndpointPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { data: instances } = useListSSHInstances();
  const instance = instances?.find((i) => i.task_id === instanceId);

  // Generate endpoint URL based on instance
  const baseEndpoint = instance?.endpoint_url || `https://ocr-${instanceId?.slice(0, 8)}.gpu.float16.cloud`;
  const apiEndpoint = `${baseEndpoint}/v1/ocr`;
  const apiToken = 'your_api_token_here';

  const curlExample = `curl -X POST ${apiEndpoint}/process \\
  --header 'Content-Type: multipart/form-data' \\
  --header 'Authorization: Bearer ${apiToken}' \\
  --form 'file=@document.pdf' \\
  --form 'task_type=default' \\
  --form 'output_format=markdown'`;

  const pythonExample = `from typhoon_ocr import ocr_document

# Process PDF document
markdown = ocr_document(
    pdf_or_image_path="document.pdf",  # Works with PDFs or images
    task_type="default",               # Choose between "default" or "structure"
    page_num=1,                        # Process page 1 (default is 1, always 1 for images)
    # Config Base URL and API Key
    base_url="${baseEndpoint}",
    api_key="${apiToken}"
)

# Process image
markdown = ocr_document(
    pdf_or_image_path="scan.jpg",      # Works with PDFs or images
    task_type="default",               # Choose between "default" or "structure"
    # Config Base URL and API Key
    base_url="${baseEndpoint}",
    api_key="${apiToken}"
)

print(markdown)`;

  const responseExample = `{
  "request_id": "req_abc123def456",
  "status": "success",
  "result": {
    "text": "# Document Title\\n\\nExtracted text content...",
    "format": "markdown",
    "document_type": "Thai ID Card",
    "confidence": 0.98,
    "entities": [
      {
        "type": "person_name",
        "value": "นายสมชาย ใจดี",
        "confidence": 0.95
      }
    ]
  },
  "usage": {
    "tokens": 245,
    "latency_ms": 324
  }
}`;

  return (
    <div className="container mx-auto pt-10">
      {/* Title & Description */}
      <h1 className="mb-2 text-2xl font-bold">API Endpoint</h1>
      <p className="mb-6 text-muted-foreground">
        Use the Typhoon OCR API to extract text from Thai documents. The API supports
        various document types including ID cards, passports, driver's licenses, and more.
      </p>

      <div className="space-y-8">
        {/* Endpoint Section */}
        <div>
          <h2 className="mb-2 text-base font-semibold">Base Endpoint</h2>
          <div className="flex max-w-[600px] items-center gap-2">
            <Input
              type="text"
              value={apiEndpoint}
              readOnly
              className="font-mono text-sm"
              endIcon={<CopyIconButton text={apiEndpoint || ''} />}
            />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            All API requests should be made to this base URL
          </p>
        </div>

        <Separator />

        {/* Authentication Section */}
        <div>
          <h2 className="mb-2 text-base font-semibold">Authentication</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Include your API token in the Authorization header for all requests:
          </p>
          <SyntaxHighlighter language="bash" showCopy>
            {`Authorization: Bearer ${apiToken}`}
          </SyntaxHighlighter>
          <p className="mt-2 text-sm text-muted-foreground">
            You can generate API tokens from the workspace credentials page.
          </p>
        </div>

        <Separator />

        {/* Example Request Section with Tabs */}
        <div>
          <h2 className="mb-4 text-base font-semibold">Example Request</h2>
          <Tabs defaultValue="python" className="w-full">
            <TabsList variant="outline">
              <TabsTrigger value="python" variant="outline">
                Python
              </TabsTrigger>
              <TabsTrigger value="curl" variant="outline">
                cURL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="python" className="mt-4">
              <SyntaxHighlighter language="python" showCopy wrapLongLines>
                {pythonExample}
              </SyntaxHighlighter>
            </TabsContent>
            <TabsContent value="curl" className="mt-4">
              <SyntaxHighlighter language="bash" showCopy wrapLongLines>
                {curlExample}
              </SyntaxHighlighter>
            </TabsContent>
          </Tabs>
        </div>

        <Separator />

        {/* Response Format Section */}
        <div>
          <h2 className="mb-4 text-base font-semibold">Response Format</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Successful requests return a JSON response with the extracted text and metadata:
          </p>
          <SyntaxHighlighter language="json" showCopy wrapLongLines>
            {responseExample}
          </SyntaxHighlighter>
        </div>

        <Separator />

        {/* Supported Document Types */}
        <div>
          <h2 className="mb-4 text-base font-semibold">Supported Document Types</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              'Thai ID Card',
              'Thai Passport',
              'Driver License',
              'Bank Statement',
              'Tax Document',
              'Business Registration',
              'Medical Record',
              'Invoice',
              'Receipt',
              'Contract',
              'General Document',
              'Handwritten Text',
            ].map((docType) => (
              <div
                key={docType}
                className="rounded-lg border bg-muted/50 px-3 py-2 text-sm"
              >
                {docType}
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Go to Docs Section */}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="text-base font-semibold">Documentation</div>
            <p className="text-sm text-muted-foreground">
              Explore our comprehensive documentation to get the most out of
              this service.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <DocLinkCardHorizontal
              title="Typhoon OCR Docs"
              description="See full API reference, parameters, and updates."
              url={OCR_DOCS_URL}
              icon={BookText}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OCRApiEndpointPage;
